import { Fragment, useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import API, { formatPrice, getImageUrl } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Download, X, Check, Search, ChevronLeft, ChevronRight, ArrowLeft, Package, Tag, PenSquare, Copy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const emptyProduct = { name: "", brand_id: "", category: "", description: "", images: [], price: 0, cost_price: 0, price_reseller: 0, discount_reseller: 0, discount_reseller_type: "percent", discount_reseller_value: 0, discount_agen: 0, discount_agen_type: "percent", discount_agen_value: 0, discount_distributor: 0, discount_distributor_type: "percent", discount_distributor_value: 0, discount_produsen: 0, discount_produsen_type: "percent", discount_produsen_value: 0, discount_mitra: 0, discount_mitra_type: "percent", discount_mitra_value: 0, dp_percentage: 50, stock_type: "ready_stock", preorder_closing_date: "", preorder_delivery_estimate: "", variants: [], status: "active", is_po_open: 1 };
const normalizeDateTimeLocalValue = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return `${text}T23:59`;
  const match = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  return match ? `${match[1]}T${match[2]}` : text;
};
const normalizeDateInputValue = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  if (!text) return "";
  const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : text;
};
const normalizeNumberInput = (value, fallback = 0) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};
const sanitizeLeadingZeroInput = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const text = String(value);
  if (/^0\d+$/.test(text)) return text.replace(/^0+/, "") || "0";
  if (/^-0\d+$/.test(text)) return `-${text.replace(/^-0+/, "") || "0"}`;
  return text;
};
const fallbackCategories = ["Gamis", "Couple", "Family Set", "Hijab", "Kids", "Men"];

const variantDiscountRoles = [
  { key: "distributor", label: "Distributor" },
  { key: "mitra", label: "Mitra" },
  { key: "agen", label: "Agen" },
  { key: "reseller", label: "Reseller" },
];

const variantDiscountValueKey = (role) => `discount_${role}_value`;
const variantDiscountTypeKey = (role) => `discount_${role}_type`;
const variantDiscountLegacyKey = (role) => role === "mitra" ? "discount_produsen" : `discount_${role}`;


const formatPoCloseShare = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T23:59` : text;
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (!match) return text.toUpperCase();
  const [, , m, d, hh, mm] = match;
  const monthNames = ["JANUARI", "FEBRUARI", "MARET", "APRIL", "MEI", "JUNI", "JULI", "AGUSTUS", "SEPTEMBER", "OKTOBER", "NOVEMBER", "DESEMBER"];
  return `${Number(d)} ${monthNames[Number(m) - 1] || m} JAM ${hh}:${mm} WIB`;
};

const formatDiscountShare = (value, type = "percent") => {
  const amount = Number(value || 0);
  if (!amount) return "";
  if (type === "fixed") return formatPrice(amount);
  return `${amount % 1 === 0 ? Math.round(amount) : amount}%`;
};

const getDiscountShareValue = (product, brand, role) => {
  const productKey = role === "agen" ? "discount_agen" : `discount_${role}`;
  const brandKey = role === "agen" ? "discount_agent" : `discount_${role}`;
  const productValue = Number(product?.[`${productKey}_value`] || product?.[productKey] || 0);
  if (productValue > 0) {
    return formatDiscountShare(productValue, product?.[`${productKey}_type`] || "percent");
  }
  const brandValue = Number(brand?.[`${brandKey}_value`] || brand?.[brandKey] || 0);
  if (brandValue > 0) {
    return formatDiscountShare(brandValue, brand?.[`${brandKey}_type`] || "percent");
  }
  return "";
};

const buildSizeAdjustmentShareLines = (product) => {
  const rows = [];
  const seen = new Set();
  (product?.variants || []).forEach((variant) => {
    const adjustment = Number(variant?.size_price_adjustment || 0);
    if (adjustment <= 0) return;
    const label = String(variant?.size || variant?.color || variant?.style || "Size khusus").trim();
    const key = `${label}|||${adjustment}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push(`${label} +${formatPrice(adjustment)}`);
  });
  return rows;
};

const buildProductShareText = (product, brand, brandLabel) => {
  const title = String(product?.name || "Produk").toUpperCase().includes(" BY ")
    ? String(product?.name || "Produk").toUpperCase()
    : `${String(product?.name || "Produk").toUpperCase()}${brandLabel ? ` BY ${String(brandLabel).toUpperCase()}` : ""}`;
  const discounts = [
    ["AGEN", getDiscountShareValue(product, brand, "agen")],
    ["RESELLER", getDiscountShareValue(product, brand, "reseller")],
  ].filter(([, value]) => value);
  const lines = [`*📝 ${title}*`];
  if (discounts.length) lines.push(`▪️DISKON: *${discounts.map(([label, value]) => `${label} ${value}`).join(" | ")}*`);
  if (product?.preorder_closing_date) lines.push(`▪️BATAS MAX ISI LINK : *${formatPoCloseShare(product.preorder_closing_date)}*`);
  if (product?.dp_percentage) lines.push(`▪️DP 1 *${Number(product.dp_percentage)}%*`);
  if (product?.preorder_delivery_estimate) lines.push(`▪️ESTIMASI READY PUSAT *${String(product.preorder_delivery_estimate).toUpperCase()}*`);
  const sizeAdjustmentLines = buildSizeAdjustmentShareLines(product);
  if (sizeAdjustmentLines.length) lines.push("", "*TAMBAHAN SIZE:*", ...sizeAdjustmentLines);
  lines.push("", "*LINK ORDER👇🏻*", `${window.location.origin}/products/${product?.id || ""}`);
  if (brand?.central_telegram_url) lines.push("", "*LINK KATALOG👇🏻*", brand.central_telegram_url);
  lines.push("", "*Isi Perlahan dan Teliti yaa, Happy Closing💖*");
  return lines.join("\n");
};


const getDeleteMessage = (response, fallback) => {
  const data = response?.data || {};
  if (data.action === "soft_deleted") return data.message || fallback;
  return data.message || fallback;
};

export default function AdminProducts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialBrandId = searchParams.get("brand_id") || "all";
  const { t, locale } = useLanguage();

  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState(fallbackCategories);
  const [selectedBrandId, setSelectedBrandId] = useState(initialBrandId);
  const [stockTypeFilter, setStockTypeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyProduct });
  const [uploading, setUploading] = useState(false);

  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchParams.get("search") || "");
  const [currentPage, setCurrentPage] = useState(Math.max(1, Number(searchParams.get("page")) || 1));
  const [productsTotal, setProductsTotal] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);
  const itemsPerPage = 10;

  const fileInputRef = useRef(null);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await API.post("/admin/import/products", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const createdBrands = Number(res.data?.created_brands || 0);
      const importedCount = Number(res.data?.imported_count || 0);
      const extraNote = createdBrands > 0 ? `, ${createdBrands} brand baru dibuat` : "";
      toast.success(`Import berhasil: ${importedCount} produk${extraNote}`);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin_products.import_failed"));
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await API.get("/admin/import/products/template", { responseType: "blob" });
      const blob = new Blob([response.data], { type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "products_import_template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal download template import produk");
    }
  };

  // Variant management state (3-level: Style → Color → Size)
  const [masterStyles, setMasterStyles] = useState([]);
  const [masterColors, setMasterColors] = useState([]);
  const [masterSizes, setMasterSizes] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [sizeStock, setSizeStock] = useState({}); // { "style|||color|||size": stock }
  const [sizePrice, setSizePrice] = useState({}); // { "style|||color|||size": price }
  const [sizeWeight, setSizeWeight] = useState({}); // { "style|||color|||size": weight_gram }
  const [sizeAdjustment, setSizeAdjustment] = useState({}); // { "style|||color|||size": size_price_adjustment }
  const [showNewStyleInput, setShowNewStyleInput] = useState(false);
  const [showNewColorInput, setShowNewColorInput] = useState(false);
  const [showNewSizeInput, setShowNewSizeInput] = useState(false);
  const [newStyleName, setNewStyleName] = useState("");
  const [newColorName, setNewColorName] = useState("");
  const [newSizeName, setNewSizeName] = useState("");

  // inline-editing variant key in summary table
  const [editingVariantKey, setEditingVariantKey] = useState(null); // "style|||color|||size"
  const [discountVariantKey, setDiscountVariantKey] = useState(null); // expanded discount row key

  const fetchProducts = useCallback(() => {
    const params = new URLSearchParams({
      limit: String(itemsPerPage),
      skip: String((currentPage - 1) * itemsPerPage),
      summary: "true",
    });
    const trimmedSearch = debouncedSearchQuery.trim();
    if (trimmedSearch) params.set("search", trimmedSearch);
    if (selectedBrandId !== "all") params.set("brand_id", selectedBrandId);
    if (stockTypeFilter !== "all") params.set("stock_type", stockTypeFilter);

    setProductsLoading(true);
    return API.get(`/products?${params.toString()}`)
      .then((r) => {
        setProducts(r.data.products || []);
        setProductsTotal(Number(r.data.total || 0));
      })
      .catch(() => { })
      .finally(() => setProductsLoading(false));
  }, [currentPage, debouncedSearchQuery, selectedBrandId, stockTypeFilter]);

  const fetchMasters = async () => {
    try {
      const [s, c, z] = await Promise.all([
        API.get("/master/styles"),
        API.get("/master/colors"),
        API.get("/master/sizes"),
      ]);
      setMasterStyles(s.data.styles || []);
      setMasterColors(c.data.colors || []);
      setMasterSizes(z.data.sizes || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    API.get("/brands").then((r) => setBrands(r.data.brands)).catch(() => { });
    API.get("/categories").then((r) => {
      const names = (r.data.categories || []).map((c) => c.name).filter(Boolean);
      setCategories(names.length ? names : fallbackCategories);
    }).catch(() => setCategories(fallbackCategories));
    fetchMasters();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const getDefaultVariantPrice = (basePrice = form.price) => {
    const normalized = normalizeNumberInput(basePrice, 0);
    return normalized > 0 ? String(normalized) : "";
  };

  const getDefaultVariantWeight = () => "100";

  // Parse existing variants to build sizeStock map
  const parseVariants = (variants, basePrice = form.price) => {
    const stockMap = {};
    const priceMap = {};
    const weightMap = {};
    const adjustmentMap = {};
    (variants || []).forEach((v) => {
      const key = `${v.style || ""}|||${v.color || ""}|||${v.size || ""}`;
      stockMap[key] = v.stock === null || v.stock === undefined ? "" : String(v.stock);
      priceMap[key] = v.price === null || v.price === undefined || v.price === "" ? getDefaultVariantPrice(basePrice) : String(v.price);
      weightMap[key] = v.weight_gram === null || v.weight_gram === undefined || v.weight_gram === "" ? getDefaultVariantWeight() : String(v.weight_gram);
      adjustmentMap[key] = v.size_price_adjustment === null || v.size_price_adjustment === undefined || v.size_price_adjustment === "" ? "0" : String(v.size_price_adjustment);
    });
    return { stockMap, priceMap, weightMap, adjustmentMap };
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyProduct, brand_id: selectedBrandId !== "all" ? selectedBrandId : "" });
    setSelectedStyle("");
    setSelectedColor("");
    setSizeStock({});
    setSizePrice({});
    setSizeWeight({});
    setSizeAdjustment({});
    setEditingVariantKey(null);
    setDiscountVariantKey(null);
    setShowNewStyleInput(false);
    setShowNewColorInput(false);
    setShowNewSizeInput(false);
    setOpen(true);
  };

  const openEdit = async (p) => {
    try {
      const { data: fullProduct } = await API.get(`/products/${p.id}`);
      setEditing(fullProduct.id);
      setForm({
        ...emptyProduct,
        ...fullProduct,
        cost_price: fullProduct.cost_price || 0,
        discount_reseller_type: fullProduct.discount_reseller_type || "percent",
        discount_reseller_value: fullProduct.discount_reseller_value ?? fullProduct.discount_reseller ?? 0,
        discount_agen_type: fullProduct.discount_agen_type || "percent",
        discount_agen_value: fullProduct.discount_agen_value ?? fullProduct.discount_agen ?? 0,
        discount_distributor_type: fullProduct.discount_distributor_type || "percent",
        discount_distributor_value: fullProduct.discount_distributor_value ?? fullProduct.discount_distributor ?? 0,
        discount_mitra_type: fullProduct.discount_mitra_type || fullProduct.discount_produsen_type || "percent",
        discount_mitra_value: fullProduct.discount_mitra_value ?? fullProduct.discount_mitra ?? fullProduct.discount_produsen_value ?? fullProduct.discount_produsen ?? 0,
        preorder_delivery_estimate: normalizeDateInputValue(fullProduct.preorder_delivery_estimate),
      });
      const { stockMap, priceMap, weightMap, adjustmentMap } = parseVariants(fullProduct.variants, fullProduct.price);
      setSizeStock(stockMap);
      setSizePrice(priceMap);
      setSizeWeight(weightMap);
      setSizeAdjustment(adjustmentMap);
      setSelectedStyle("");
      setSelectedColor("");
      setEditingVariantKey(null);
      setDiscountVariantKey(null);
      setShowNewStyleInput(false);
      setShowNewColorInput(false);
      setShowNewSizeInput(false);
      setOpen(true);
    } catch {
      toast.error(locale === "id" ? "Gagal mengambil detail produk" : "Failed to load product detail");
    }
  };

  // Get unique styles from current form variants
  const getUsedStyles = () => [...new Set((form.variants || []).map(v => v.style || "").filter(Boolean))];
  // Get unique colors for a given style
  const getUsedColorsForStyle = (style) => [...new Set((form.variants || []).filter(v => (v.style || "") === (style || "")).map(v => v.color || "").filter(Boolean))];
  // Get sizes for a style+color combo
  const getSizesForStyleColor = (style, color) => {
    return (form.variants || []).filter(v => (v.style || "") === (style || "") && v.color === color).map(v => v.size).filter(Boolean);
  };

  // Add a size to the current style+color
  const handleAddSizeToVariant = (sizeName) => {
    if (!selectedColor) {
      toast.error(locale === "id" ? "Pilih warna terlebih dahulu" : "Please select color first");
      return;
    }
    const key = `${selectedStyle}|||${selectedColor}|||${sizeName}`;
    const existing = (form.variants || []).find(v => (v.style || "") === (selectedStyle || "") && v.color === selectedColor && v.size === sizeName);
    if (existing) return; // Already exists
    const defaultPrice = getDefaultVariantPrice();
    const defaultWeight = getDefaultVariantWeight();
    setForm(prev => ({
      ...prev,
      variants: [...(prev.variants || []), { style: selectedStyle, color: selectedColor, size: sizeName, stock: 0, price: defaultPrice, size_price_adjustment: 0, weight_gram: defaultWeight }]
    }));
    setSizeStock(prev => ({ ...prev, [key]: 0 }));
    setSizePrice(prev => ({ ...prev, [key]: defaultPrice }));
    setSizeAdjustment(prev => ({ ...prev, [key]: 0 }));
    setSizeWeight(prev => ({ ...prev, [key]: defaultWeight }));
  };

  // Toggle size (add or remove)
  const handleToggleSize = (sizeName) => {
    if (!selectedColor) return;
    const key = `${selectedStyle}|||${selectedColor}|||${sizeName}`;
    const existing = (form.variants || []).find(v => (v.style || "") === (selectedStyle || "") && v.color === selectedColor && v.size === sizeName);
    if (existing) {
      // Remove
      setForm(prev => ({
        ...prev,
        variants: (prev.variants || []).filter(v => !((v.style || "") === (selectedStyle || "") && v.color === selectedColor && v.size === sizeName))
      }));
      setSizeStock(prev => { const n = { ...prev }; delete n[key]; return n; });
      setSizePrice(prev => { const n = { ...prev }; delete n[key]; return n; });
      setSizeWeight(prev => { const n = { ...prev }; delete n[key]; return n; });
      setSizeAdjustment(prev => { const n = { ...prev }; delete n[key]; return n; });
      if (editingVariantKey === key) setEditingVariantKey(null);
      if (discountVariantKey === key) setDiscountVariantKey(null);
    } else {
      // Add
      handleAddSizeToVariant(sizeName);
    }
  };

  const handleStockChange = (sizeName, value) => {
    if (!selectedColor) return;
    const key = `${selectedStyle}|||${selectedColor}|||${sizeName}`;
    const nextValue = sanitizeLeadingZeroInput(value);
    setSizeStock(prev => ({ ...prev, [key]: nextValue }));
    setForm(prev => {
      const newVariants = [...(prev.variants || [])];
      const idx = newVariants.findIndex(v => (v.style || "") === (selectedStyle || "") && v.color === selectedColor && v.size === sizeName);
      if (idx >= 0) {
        newVariants[idx] = { ...newVariants[idx], stock: nextValue };
      }
      return { ...prev, variants: newVariants };
    });
  };

  const handlePriceChange = (sizeName, value) => {
    if (!selectedColor) return;
    const key = `${selectedStyle}|||${selectedColor}|||${sizeName}`;
    const nextValue = sanitizeLeadingZeroInput(value);
    setSizePrice(prev => ({ ...prev, [key]: nextValue }));
    setForm(prev => {
      const newVariants = [...(prev.variants || [])];
      const idx = newVariants.findIndex(v => (v.style || "") === (selectedStyle || "") && v.color === selectedColor && v.size === sizeName);
      if (idx >= 0) {
        newVariants[idx] = { ...newVariants[idx], price: nextValue };
      }
      return { ...prev, variants: newVariants };
    });
  };

  const handleAdjustmentChange = (sizeName, value) => {
    if (!selectedColor) return;
    const key = `${selectedStyle}|||${selectedColor}|||${sizeName}`;
    const nextValue = sanitizeLeadingZeroInput(value);
    setSizeAdjustment(prev => ({ ...prev, [key]: nextValue }));
    setForm(prev => {
      const newVariants = [...(prev.variants || [])];
      const idx = newVariants.findIndex(v => (v.style || "") === (selectedStyle || "") && v.color === selectedColor && v.size === sizeName);
      if (idx >= 0) {
        newVariants[idx] = { ...newVariants[idx], size_price_adjustment: nextValue };
      }
      return { ...prev, variants: newVariants };
    });
  };

  const handleRemoveVariant = (style, color, size) => {
    const key = `${style}|||${color}|||${size}`;
    setForm(prev => ({
      ...prev,
      variants: (prev.variants || []).filter(v => !((v.style || "") === (style || "") && v.color === color && v.size === size))
    }));
    setSizeStock(prev => { const n = { ...prev }; delete n[key]; return n; });
    setSizePrice(prev => { const n = { ...prev }; delete n[key]; return n; });
    setSizeWeight(prev => { const n = { ...prev }; delete n[key]; return n; });
    setSizeAdjustment(prev => { const n = { ...prev }; delete n[key]; return n; });
    if (editingVariantKey === key) setEditingVariantKey(null);
    if (discountVariantKey === key) setDiscountVariantKey(null);
  };

  // Edit stock directly from summary table
  const handleSummaryStockChange = (style, color, size, value) => {
    const key = `${style}|||${color}|||${size}`;
    const nextValue = sanitizeLeadingZeroInput(value);
    setSizeStock(prev => ({ ...prev, [key]: nextValue }));
    setForm(prev => ({
      ...prev,
      variants: (prev.variants || []).map(v =>
        (v.style || "") === (style || "") && v.color === color && v.size === size
          ? { ...v, stock: nextValue }
          : v
      ),
    }));
    setEditingVariantKey(key);
  };

  // Edit price directly from summary table
  const handleSummaryPriceChange = (style, color, size, value) => {
    const key = `${style}|||${color}|||${size}`;
    const nextValue = sanitizeLeadingZeroInput(value);
    setSizePrice(prev => ({ ...prev, [key]: nextValue }));
    setForm(prev => ({
      ...prev,
      variants: (prev.variants || []).map(v =>
        (v.style || "") === (style || "") && v.color === color && v.size === size
          ? { ...v, price: nextValue }
          : v
      ),
    }));
    setEditingVariantKey(key);
  };

  const handleSummaryAdjustmentChange = (style, color, size, value) => {
    const key = `${style}|||${color}|||${size}`;
    const nextValue = sanitizeLeadingZeroInput(value);
    setSizeAdjustment(prev => ({ ...prev, [key]: nextValue }));
    setForm(prev => ({
      ...prev,
      variants: (prev.variants || []).map(v =>
        (v.style || "") === (style || "") && v.color === color && v.size === size
          ? { ...v, size_price_adjustment: nextValue }
          : v
      ),
    }));
    setEditingVariantKey(key);
  };

  const handleWeightChange = (sizeName, value) => {
    if (!selectedColor) return;
    const key = `${selectedStyle}|||${selectedColor}|||${sizeName}`;
    const nextValue = sanitizeLeadingZeroInput(value);
    setSizeWeight(prev => ({ ...prev, [key]: nextValue }));
    setForm(prev => {
      const newVariants = [...(prev.variants || [])];
      const idx = newVariants.findIndex(v => (v.style || "") === (selectedStyle || "") && v.color === selectedColor && v.size === sizeName);
      if (idx >= 0) {
        newVariants[idx] = { ...newVariants[idx], weight_gram: nextValue };
      }
      return { ...prev, variants: newVariants };
    });
  };

  const handleSummaryWeightChange = (style, color, size, value) => {
    const key = `${style}|||${color}|||${size}`;
    const nextValue = sanitizeLeadingZeroInput(value);
    setSizeWeight(prev => ({ ...prev, [key]: nextValue }));
    setForm(prev => ({
      ...prev,
      variants: (prev.variants || []).map(v =>
        (v.style || "") === (style || "") && v.color === color && v.size === size
          ? { ...v, weight_gram: nextValue }
          : v
      ),
    }));
    setEditingVariantKey(key);
  };

  const getVariantDiscountBasePrice = (variant) => {
    const variantPrice = normalizeNumberInput(variant?.price, normalizeNumberInput(form.price, 0));
    return Math.max(0, variantPrice);
  };

  const getVariantDiscountValue = (variant, role) => {
    const valueKey = variantDiscountValueKey(role);
    const legacyKey = variantDiscountLegacyKey(role);
    const rawValue = variant?.[valueKey] ?? variant?.[legacyKey] ?? "";
    return rawValue === null || rawValue === undefined ? "" : String(rawValue);
  };

  const getVariantDiscountType = (variant, role) => {
    const typeKey = variantDiscountTypeKey(role);
    const legacyKey = `${variantDiscountLegacyKey(role)}_type`;
    return variant?.[typeKey] || variant?.[legacyKey] || "percent";
  };

  const getVariantDiscountAmountPreview = (variant, role) => {
    const value = normalizeNumberInput(getVariantDiscountValue(variant, role), 0);
    if (value <= 0) return 0;
    const type = getVariantDiscountType(variant, role);
    const basePrice = getVariantDiscountBasePrice(variant);
    if (type === "fixed") return Math.min(basePrice, value);
    return Math.min(basePrice, (basePrice * value) / 100);
  };

  const getVariantDiscountStatus = (variant) => {
    const customRoles = variantDiscountRoles.filter(({ key }) => String(getVariantDiscountValue(variant, key)).trim() !== "");
    if (!customRoles.length) return { label: "Ikut Produk", tone: "secondary" };
    const zeroRoles = customRoles.filter(({ key }) => normalizeNumberInput(getVariantDiscountValue(variant, key), 0) === 0);
    if (zeroRoles.length === customRoles.length) return { label: `No Disc ${zeroRoles.length}`, tone: "outline" };
    return { label: `Custom ${customRoles.length}`, tone: "default" };
  };

  const handleToggleVariantDiscount = (key) => {
    setDiscountVariantKey((prev) => (prev === key ? null : key));
  };

  const handleVariantDiscountTypeChange = (style, color, size, role, nextType) => {
    setForm((prev) => ({
      ...prev,
      variants: (prev.variants || []).map((v) => {
        if (!((v.style || "") === (style || "") && v.color === color && v.size === size)) return v;
        const basePrice = getVariantDiscountBasePrice(v);
        const currentType = getVariantDiscountType(v, role);
        const currentValue = normalizeNumberInput(getVariantDiscountValue(v, role), 0);
        let nextValue = currentValue;
        if (currentValue > 0) {
          nextValue = nextType === "fixed"
            ? (currentType === "percent" ? Math.round((basePrice * currentValue) / 100) : currentValue)
            : (currentType === "fixed" && basePrice > 0 ? Number((((currentValue / basePrice) * 100)).toFixed(2)) : currentValue);
        }
        return {
          ...v,
          [variantDiscountTypeKey(role)]: nextType,
          [variantDiscountValueKey(role)]: nextValue > 0 ? nextValue : currentValue === 0 ? 0 : "",
        };
      }),
    }));
  };

  const handleVariantDiscountValueChange = (style, color, size, role, value) => {
    const nextValue = sanitizeLeadingZeroInput(value);
    setForm((prev) => ({
      ...prev,
      variants: (prev.variants || []).map((v) => {
        if (!((v.style || "") === (style || "") && v.color === color && v.size === size)) return v;
        return {
          ...v,
          [variantDiscountValueKey(role)]: nextValue,
        };
      }),
    }));
  };

  const handleVariantDiscountValueBlur = (style, color, size, role, value) => {
    setForm((prev) => ({
      ...prev,
      variants: (prev.variants || []).map((v) => {
        if (!((v.style || "") === (style || "") && v.color === color && v.size === size)) return v;
        const normalized = value === "" ? "" : normalizeNumberInput(value, 0);
        return {
          ...v,
          [variantDiscountValueKey(role)]: normalized,
        };
      }),
    }));
  };

  const handleResetVariantDiscount = (style, color, size) => {
    setForm((prev) => ({
      ...prev,
      variants: (prev.variants || []).map((v) => {
        if (!((v.style || "") === (style || "") && v.color === color && v.size === size)) return v;
        const nextVariant = { ...v };
        variantDiscountRoles.forEach(({ key }) => {
          delete nextVariant[variantDiscountValueKey(key)];
          delete nextVariant[variantDiscountTypeKey(key)];
        });
        return nextVariant;
      }),
    }));
  };

  const getVariantNetPricePreview = (variant, role) => {
    const basePrice = getVariantDiscountBasePrice(variant);
    const adjustment = normalizeNumberInput(variant?.size_price_adjustment, 0);
    const discountAmount = getVariantDiscountAmountPreview(variant, role);
    return Math.max(0, basePrice - discountAmount) + adjustment;
  };

  // Inline add to master and select
  const handleQuickAddStyle = async () => {
    if (!newStyleName.trim()) return;
    try {
      await API.post("/master/styles", { name: newStyleName.trim() });
      await fetchMasters();
      setSelectedStyle(newStyleName.trim());
      setNewStyleName("");
      setShowNewStyleInput(false);
      toast.success(t("admin_master.added"));
    } catch { toast.error("Failed"); }
  };

  const handleQuickAddColor = async () => {
    if (!newColorName.trim()) return;
    try {
      await API.post("/master/colors", { name: newColorName.trim() });
      await fetchMasters();
      setSelectedColor(newColorName.trim());
      setNewColorName("");
      setShowNewColorInput(false);
      toast.success(t("admin_master.added"));
    } catch { toast.error("Failed"); }
  };

  const handleQuickAddSize = async () => {
    if (!newSizeName.trim()) return;
    try {
      await API.post("/master/sizes", { name: newSizeName.trim() });
      await fetchMasters();
      handleAddSizeToVariant(newSizeName.trim());
      setNewSizeName("");
      setShowNewSizeInput(false);
      toast.success(t("admin_master.added"));
    } catch { toast.error("Failed"); }
  };

  // Shortcut: add all master sizes to current style+color
  const handleAddAllSizes = () => {
    if (!selectedColor) return;
    masterSizes.forEach(s => {
      const existing = (form.variants || []).find(v => (v.style || "") === (selectedStyle || "") && v.color === selectedColor && v.size === s.name);
      if (!existing) {
        handleAddSizeToVariant(s.name);
      }
    });
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await API.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        uploadedUrls.push(data.url || data.path);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...uploadedUrls] }));
      toast.success(t("admin_products.image_uploaded"));
    } catch { toast.error(t("admin_products.upload_failed")); }
    finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSave = async (options = {}) => {
    const keepOpen = options?.keepOpen === true;
    try {
      const payload = {
        ...form,
        price: normalizeNumberInput(form.price, 0),
        cost_price: normalizeNumberInput(form.cost_price, 0),
        discount_reseller_value: normalizeNumberInput(form.discount_reseller_value, 0),
        discount_agen_value: normalizeNumberInput(form.discount_agen_value, 0),
        discount_distributor_value: normalizeNumberInput(form.discount_distributor_value, 0),
        discount_mitra_value: normalizeNumberInput(form.discount_mitra_value, 0),
        discount_reseller: (form.discount_reseller_type || "percent") === "percent" ? normalizeNumberInput(form.discount_reseller_value, 0) : 0,
        discount_agen: (form.discount_agen_type || "percent") === "percent" ? normalizeNumberInput(form.discount_agen_value, 0) : 0,
        discount_distributor: (form.discount_distributor_type || "percent") === "percent" ? normalizeNumberInput(form.discount_distributor_value, 0) : 0,
        discount_mitra: (form.discount_mitra_type || "percent") === "percent" ? normalizeNumberInput(form.discount_mitra_value, 0) : 0,
        dp_percentage: normalizeNumberInput(form.dp_percentage, 0),
        variants: (form.variants || []).map((variant) => ({
          ...variant,
          stock: Math.max(0, parseInt(variant.stock, 10) || 0),
          price: variant.price === "" || variant.price === null || variant.price === undefined ? undefined : Math.max(0, normalizeNumberInput(variant.price, 0)),
          size_price_adjustment: Math.max(0, normalizeNumberInput(variant.size_price_adjustment, 0)),
          weight_gram: Math.max(1, parseInt(variant.weight_gram, 10) || 100),
          discount_distributor_type: getVariantDiscountType(variant, "distributor"),
          discount_distributor_value: variant.discount_distributor_value === "" || variant.discount_distributor_value === null || variant.discount_distributor_value === undefined ? undefined : Math.max(0, normalizeNumberInput(variant.discount_distributor_value, 0)),
          discount_mitra_type: getVariantDiscountType(variant, "mitra"),
          discount_mitra_value: variant.discount_mitra_value === "" || variant.discount_mitra_value === null || variant.discount_mitra_value === undefined ? undefined : Math.max(0, normalizeNumberInput(variant.discount_mitra_value, 0)),
          discount_agen_type: getVariantDiscountType(variant, "agen"),
          discount_agen_value: variant.discount_agen_value === "" || variant.discount_agen_value === null || variant.discount_agen_value === undefined ? undefined : Math.max(0, normalizeNumberInput(variant.discount_agen_value, 0)),
          discount_reseller_type: getVariantDiscountType(variant, "reseller"),
          discount_reseller_value: variant.discount_reseller_value === "" || variant.discount_reseller_value === null || variant.discount_reseller_value === undefined ? undefined : Math.max(0, normalizeNumberInput(variant.discount_reseller_value, 0)),
        })),
      };
      if (editing) {
        const { data: savedProduct } = await API.put(`/products/${editing}`, payload);
        if (keepOpen && savedProduct) {
          setForm((prev) => ({ ...prev, ...savedProduct }));
        }
        toast.success(t("common.saved") || "Product updated!");
      } else {
        await API.post("/products", payload);
        toast.success(t("common.saved") || "Product created!");
      }
      if (!keepOpen) {
        setOpen(false);
        setEditingVariantKey(null);
        setDiscountVariantKey(null);
      }
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("admin_products.confirm_delete"))) return;
    try {
      const response = await API.delete(`/products/${id}`);
      toast.success(getDeleteMessage(response, t("admin_products.deleted") || "Product deleted"));
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const currentBrand = brands.find((b) => b.id === selectedBrandId);
  const getBrandLabel = (product) => {
    const matchedBrand = brands.find((b) => b.id === product?.brand_id);
    return product?.brand_name || product?.brand?.name || matchedBrand?.name || product?.brand_id?.replace("brand_", "") || "";
  };


  const getBrandForProduct = (product) => product?.brand || brands.find((b) => b.id === product?.brand_id) || {};

  const handleCopyProductShare = async (product) => {
    try {
      const { data: fullProduct } = await API.get(`/products/${product.id}`);
      const sourceProduct = fullProduct || product;
      const brand = getBrandForProduct(sourceProduct);
      const text = buildProductShareText(sourceProduct, brand, getBrandLabel(sourceProduct));
      await navigator.clipboard.writeText(text);
      toast.success(locale === "id" ? "Detail produk berhasil disalin" : "Product details copied");
    } catch {
      toast.error(locale === "id" ? "Gagal menyalin detail produk" : "Failed to copy product details");
    }
  };

  const totalPages = Math.max(1, Math.ceil(productsTotal / itemsPerPage));
  const paginatedProducts = products;

  const handleBrandFilterChange = (val) => {
    setSelectedBrandId(val);
    setCurrentPage(1);
  };

  const handleStockTypeFilterChange = (val) => {
    setStockTypeFilter(val);
    setCurrentPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (selectedBrandId !== "all") newParams.set("brand_id", selectedBrandId);
    if (stockTypeFilter !== "all") newParams.set("stock_type", stockTypeFilter);
    if (debouncedSearchQuery.trim()) newParams.set("search", debouncedSearchQuery.trim());
    if (currentPage > 1) newParams.set("page", String(currentPage));
    setSearchParams(newParams, { replace: true });
  }, [currentPage, debouncedSearchQuery, selectedBrandId, setSearchParams, stockTypeFilter]);



  return (
    <AdminLayout title={t("admin_nav.products")}>
      <div data-testid="admin-products">
        <div className="flex flex-col gap-4 mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <p className="text-sm text-stone-500 whitespace-nowrap">{productsTotal} {t("admin_nav.products").toLowerCase()}</p>
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                ref={fileInputRef}
                onChange={handleImport}
                disabled={uploading}
              />
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="bg-white border border-stone-200 text-stone-600 px-4 h-9 text-xs font-semibold flex items-center justify-center gap-2 hover:bg-stone-50 rounded-md transition-colors whitespace-nowrap"
              >
                <Download className="w-3.5 h-3.5 text-stone-400" /> Download Template
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-white border border-stone-200 text-stone-600 px-4 h-9 text-xs font-semibold flex items-center justify-center gap-2 hover:bg-stone-50 rounded-md transition-colors whitespace-nowrap disabled:opacity-50"
              >
                <Upload className="w-3.5 h-3.5 text-stone-400" /> {uploading ? t("admin_products.importing") : t("admin_products.import_excel")}
              </button>
              <button onClick={openCreate} className="bg-[#8A9A86] text-white px-5 h-9 text-xs font-bold flex items-center justify-center gap-2 hover:bg-[#768573] rounded-md transition-colors whitespace-nowrap" data-testid="add-product-btn">
                <Plus className="w-4 h-4" /> {t("admin_products.add_product")}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 bg-white border border-stone-200 rounded-lg shadow-sm">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
              <Input
                type="text"
                placeholder={t("common.search")}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 h-10 text-sm border-stone-200 focus:ring-[#8A9A86]"
              />
            </div>

            {/* Brand Filter */}
            <Select value={selectedBrandId} onValueChange={handleBrandFilterChange}>
              <SelectTrigger className="h-10 text-sm border-stone-200 focus:ring-[#8A9A86]">
                <div className="flex items-center gap-2">
                  <Tag className="w-3.5 h-3.5 text-stone-400" />
                  <SelectValue placeholder={t("admin_products.filter_brand")} />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.all_brands") || "Semua Merek"}</SelectItem>
                {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Stock Type Filter */}
            <Select value={stockTypeFilter} onValueChange={handleStockTypeFilterChange}>
              <SelectTrigger className="h-10 text-sm border-stone-200 focus:ring-[#8A9A86]">
                <div className="flex items-center gap-2">
                  <Package className="w-3.5 h-3.5 text-stone-400" />
                  <SelectValue placeholder={t("admin_products.filter_stock_type")} />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("admin_products.all_stock_types")}</SelectItem>
                <SelectItem value="ready_stock">{t("home.ready_stock")}</SelectItem>
                <SelectItem value="preorder">{t("home.preorder")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Reset Button (Optional) */}
            {(selectedBrandId !== "all" || stockTypeFilter !== "all" || searchQuery) && (
              <button
                onClick={() => { handleSearchChange(""); handleBrandFilterChange("all"); handleStockTypeFilterChange("all"); }}
                className="text-xs text-stone-400 hover:text-stone-600 font-medium underline underline-offset-4"
              >
                Reset Filters
              </button>
            )}
          </div>
        </div>

        <div className="bg-white border border-stone-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-stone-100 bg-stone-50/50">
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_products.product")}</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_products.category")}</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_products.price")}</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_products.type")}</th>
                <th className="text-right p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_products.actions")}</th>
              </tr></thead>
              <tbody>
                {productsLoading && (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-sm text-stone-400">Loading products...</td>
                  </tr>
                )}
                {!productsLoading && paginatedProducts.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-sm text-stone-400">No products found</td>
                  </tr>
                )}
                {!productsLoading && paginatedProducts.map((p) => (
                  <tr key={p.id} className="border-b-2 border-stone-200/80 hover:bg-stone-50/50" data-testid={`product-row-${p.id}`}>
                    <td className="py-4 px-3 sm:p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-12 bg-stone-100 flex-shrink-0 overflow-hidden">
                          {p.images?.[0] && <img src={getImageUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <span className="font-medium text-stone-800">{p.name} by {getBrandLabel(p)}</span>
                      </div>
                    </td>
                    <td className="py-4 px-3 sm:p-3 text-stone-600">{p.category}</td>
                    <td className="py-4 px-3 sm:p-3 text-stone-800">{formatPrice(p.price)}</td>
                    <td className="py-4 px-3 sm:p-3">
                      {p.stock_type === "preorder" ? (
                        <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 text-[10px]">{t("home.preorder")}</Badge>
                      ) : (
                        <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 text-[10px]">Ready</Badge>
                      )}
                    </td>
                    <td className="py-4 px-3 sm:p-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2 sm:gap-1.5 flex-wrap">
                        {p.stock_type === "preorder" && (
                          <button
                            type="button"
                            onClick={() => handleCopyProductShare(p)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-stone-400 transition-colors hover:bg-stone-100 hover:text-[#8A9A86]"
                            data-testid={`copy-product-${p.id}`}
                            title={locale === "id" ? "Copy detail PO" : "Copy PO details"}
                            aria-label={locale === "id" ? "Salin detail PO" : "Copy PO details"}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => openEdit(p)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700"
                          data-testid={`edit-product-${p.id}`}
                          aria-label={locale === "id" ? "Edit produk" : "Edit product"}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(p.id)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-stone-400 transition-colors hover:bg-red-50 hover:text-red-500"
                          data-testid={`delete-product-${p.id}`}
                          aria-label={locale === "id" ? "Hapus produk" : "Delete product"}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-stone-100 pt-4 mt-6">
            <p className="text-sm text-stone-500">{t("common.page")} {currentPage} {t("common.of")} {totalPages} · {productsTotal} products</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="w-[95vw] max-w-6xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">{editing ? t("admin_products.edit_product") : t("admin_products.new_product")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_products.name")}</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="product-name-input" />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_products.category")}</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder={t("admin_products.select_category")} /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs uppercase tracking-wider text-stone-500">Brand</Label>
                  <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder={locale === "id" ? "Pilih Brand" : "Select Brand"} /></SelectTrigger>
                    <SelectContent>
                      {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_products.stock_type")}</Label>
                <Select value={form.stock_type} onValueChange={(v) => setForm({ ...form, stock_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ready_stock">{t("home.ready_stock")}</SelectItem>
                    <SelectItem value="preorder">{t("home.preorder")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_products.desc")}</Label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
                  className="w-full mt-1 px-3 py-2 text-sm border border-stone-200 focus:outline-none focus:border-[#8A9A86]" data-testid="product-desc-input" />
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_products.price_retail")}</Label>
                  <Input type="number" value={form.price ?? ""} onChange={(e) => setForm({ ...form, price: sanitizeLeadingZeroInput(e.target.value) })} onBlur={(e) => setForm((prev) => ({ ...prev, price: normalizeNumberInput(e.target.value, 0) }))} className="mt-1" data-testid="product-price-input" />
                  <p className="mt-1 text-[11px] text-stone-400">{locale === "id" ? "Ini jadi harga pusat / base price utama. Semua diskon role dihitung dari angka ini." : "This becomes the central/base price. All role discounts are computed from this value."}</p>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500 mb-2 block">{locale === "id" ? "Diskon Per Role" : "Discount Per Role"}</Label>
                <p className="text-[11px] text-stone-400 mb-2">{locale === "id" ? "Semua diskon role dihitung dari harga normal pusat. Isi 0 untuk fallback ke diskon brand. Tipe bisa persen atau nominal fix." : "All role discounts are computed from the central base price. Set 0 to fallback to brand discount. Type can be percent or fixed."}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-stone-500">Reseller</label>
                    <div className="flex gap-2 mt-0.5">
                      <Select value={form.discount_reseller_type || "percent"} onValueChange={(v) => setForm({ ...form, discount_reseller_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="percent">Percent</SelectItem><SelectItem value="fixed">Fixed</SelectItem></SelectContent>
                      </Select>
                      <Input type="number" min="0" step="0.1" value={form.discount_reseller_value ?? form.discount_reseller ?? ""}
                        onChange={(e) => {
                          const nextValue = sanitizeLeadingZeroInput(e.target.value);
                          setForm({ ...form, discount_reseller_value: nextValue, discount_reseller: (form.discount_reseller_type || "percent") === "percent" ? nextValue : 0 });
                        }}
                        onBlur={(e) => setForm((prev) => {
                          const value = normalizeNumberInput(e.target.value, 0);
                          return { ...prev, discount_reseller_value: value, discount_reseller: (prev.discount_reseller_type || "percent") === "percent" ? value : 0 };
                        })} />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] text-stone-500">Agen</label>
                    <div className="flex gap-2 mt-0.5">
                      <Select value={form.discount_agen_type || "percent"} onValueChange={(v) => setForm({ ...form, discount_agen_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="percent">Percent</SelectItem><SelectItem value="fixed">Fixed</SelectItem></SelectContent>
                      </Select>
                      <Input type="number" min="0" step="0.1" value={form.discount_agen_value ?? form.discount_agen ?? ""}
                        onChange={(e) => {
                          const nextValue = sanitizeLeadingZeroInput(e.target.value);
                          setForm({ ...form, discount_agen_value: nextValue, discount_agen: (form.discount_agen_type || "percent") === "percent" ? nextValue : 0 });
                        }}
                        onBlur={(e) => setForm((prev) => {
                          const value = normalizeNumberInput(e.target.value, 0);
                          return { ...prev, discount_agen_value: value, discount_agen: (prev.discount_agen_type || "percent") === "percent" ? value : 0 };
                        })} />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] text-stone-500">Distributor</label>
                    <div className="flex gap-2 mt-0.5">
                      <Select value={form.discount_distributor_type || "percent"} onValueChange={(v) => setForm({ ...form, discount_distributor_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="percent">Percent</SelectItem><SelectItem value="fixed">Fixed</SelectItem></SelectContent>
                      </Select>
                      <Input type="number" min="0" step="0.1" value={form.discount_distributor_value ?? form.discount_distributor ?? ""}
                        onChange={(e) => {
                          const nextValue = sanitizeLeadingZeroInput(e.target.value);
                          setForm({ ...form, discount_distributor_value: nextValue, discount_distributor: (form.discount_distributor_type || "percent") === "percent" ? nextValue : 0 });
                        }}
                        onBlur={(e) => setForm((prev) => {
                          const value = normalizeNumberInput(e.target.value, 0);
                          return { ...prev, discount_distributor_value: value, discount_distributor: (prev.discount_distributor_type || "percent") === "percent" ? value : 0 };
                        })} />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] text-stone-500">Mitra</label>
                    <div className="flex gap-2 mt-0.5">
                      <Select value={form.discount_mitra_type || "percent"} onValueChange={(v) => setForm({ ...form, discount_mitra_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="percent">Percent</SelectItem><SelectItem value="fixed">Fixed</SelectItem></SelectContent>
                      </Select>
                      <Input type="number" min="0" step="0.1" value={form.discount_mitra_value ?? form.discount_mitra ?? ""}
                        onChange={(e) => {
                          const nextValue = sanitizeLeadingZeroInput(e.target.value);
                          setForm({ ...form, discount_mitra_value: nextValue, discount_mitra: (form.discount_mitra_type || "percent") === "percent" ? nextValue : 0 });
                        }}
                        onBlur={(e) => setForm((prev) => {
                          const value = normalizeNumberInput(e.target.value, 0);
                          return { ...prev, discount_mitra_value: value, discount_mitra: (prev.discount_mitra_type || "percent") === "percent" ? value : 0 };
                        })} />
                    </div>
                  </div>
                </div>
              </div>
              {form.stock_type === "preorder" && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_products.closing_date")} (WIB)</Label>
                      <Input type="datetime-local" value={normalizeDateTimeLocalValue(form.preorder_closing_date)} onChange={(e) => setForm({ ...form, preorder_closing_date: e.target.value })} className="mt-1" data-testid="preorder-date-input" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-stone-500">Estimate Ready</Label>
                      <Input type="date" value={normalizeDateInputValue(form.preorder_delivery_estimate)} onChange={(e) => setForm({ ...form, preorder_delivery_estimate: e.target.value })} className="mt-1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-stone-500 mb-1 block">Persentase DP (%)</Label>
                      <Input type="number" min="0" max="100" value={form.dp_percentage ?? ""} onChange={(e) => setForm({ ...form, dp_percentage: sanitizeLeadingZeroInput(e.target.value) })} onBlur={(e) => setForm((prev) => ({ ...prev, dp_percentage: normalizeNumberInput(e.target.value, 0) }))} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-stone-500 mb-1 block">{t("admin_products.po_status")}</Label>
                      <Select value={String(form.is_po_open ?? 1)} onValueChange={(v) => setForm({ ...form, is_po_open: Number(v) })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">{t("admin_products.po_open")}</SelectItem>
                          <SelectItem value="0">{t("admin_products.po_closed")}</SelectItem>
                          <SelectItem value="2">{t("admin_products.po_ready")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-[10px] text-stone-400 mt-1">{t("admin_products.po_status_desc")}</p>
                </>
              )}
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_products.images")}</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.images?.map((img, i) => (
                    <div key={i} className="relative w-16 h-20 bg-stone-100 overflow-hidden">
                      <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setForm({ ...form, images: form.images.filter((_, idx) => idx !== i) })} className="absolute top-0 right-0 bg-red-500 text-white w-4 h-4 flex items-center justify-center text-[10px]">x</button>
                    </div>
                  ))}
                  <label className="w-16 h-20 border-2 border-dashed border-stone-200 flex items-center justify-center cursor-pointer hover:border-[#8A9A86]">
                    <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
                    {uploading ? <span className="text-xs text-stone-400">...</span> : <Upload className="w-4 h-4 text-stone-400" />}
                  </label>
                </div>
              </div>

              {/* Variant Management Section (3-Level: Style → Color → Size) */}
              <div className="border border-stone-200 rounded-lg p-4 space-y-4 bg-stone-50/50">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_products.variants_stock")}</Label>
                  <span className="text-xs text-stone-400">{(form.variants || []).length} {locale === "id" ? "kombinasi" : "combination(s)"}</span>
                </div>

                {/* Step 1: Style Selection */}
                <div className="space-y-2">
                  <Label className="text-xs text-stone-500">1. {t("admin_products.style") || "Style"}</Label>
                  <div className="flex gap-2">
                    <Select value={selectedStyle} onValueChange={(v) => setSelectedStyle(v)}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder={locale === "id" ? "Pilih Style..." : "Select Style..."} /></SelectTrigger>
                      <SelectContent>
                        {masterStyles.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {showNewStyleInput ? (
                      <div className="flex gap-1 flex-1">
                        <Input
                          value={newStyleName}
                          onChange={(e) => setNewStyleName(e.target.value)}
                          placeholder={t("admin_master.placeholder_style")}
                          className="flex-1"
                          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleQuickAddStyle())}
                        />
                        <Button size="sm" onClick={handleQuickAddStyle} variant="default" className="bg-[#8A9A86]"><Check className="w-4 h-4" /></Button>
                        <Button size="sm" onClick={() => { setShowNewStyleInput(false); setNewStyleName(""); }} variant="outline"><X className="w-4 h-4" /></Button>
                      </div>
                    ) : (
                      <Button size="sm" onClick={() => setShowNewStyleInput(true)} variant="outline"><Plus className="w-4 h-4 mr-1" />{t("admin_products.new_btn")}</Button>
                    )}
                  </div>
                </div>

                {/* Step 2: Color Selection */}
                <div className="space-y-2">
                    <Label className="text-xs text-stone-500">2. {t("admin_products.select_add_variant")}</Label>
                    <div className="flex gap-2">
                      <Select value={selectedColor} onValueChange={(v) => setSelectedColor(v)}>
                        <SelectTrigger className="flex-1"><SelectValue placeholder={locale === "id" ? "Pilih Warna..." : "Select Color..."} /></SelectTrigger>
                        <SelectContent>
                          {masterColors.map((c) => (
                            <SelectItem key={c.id} value={c.name}>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full border border-stone-300" style={{ backgroundColor: c.hex_code || "#ccc" }} />
                                {c.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {showNewColorInput ? (
                        <div className="flex gap-1 flex-1">
                          <Input
                            value={newColorName}
                            onChange={(e) => setNewColorName(e.target.value)}
                            placeholder={t("admin_master.placeholder_color")}
                            className="flex-1"
                            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleQuickAddColor())}
                          />
                          <Button size="sm" onClick={handleQuickAddColor} variant="default" className="bg-[#8A9A86]"><Check className="w-4 h-4" /></Button>
                          <Button size="sm" onClick={() => { setShowNewColorInput(false); setNewColorName(""); }} variant="outline"><X className="w-4 h-4" /></Button>
                        </div>
                      ) : (
                        <Button size="sm" onClick={() => setShowNewColorInput(true)} variant="outline"><Plus className="w-4 h-4 mr-1" />{t("admin_products.new_btn")}</Button>
                      )}
                    </div>
                  </div>

                {/* Step 3: Size & Stock (shown after Color selected) */}
                {selectedColor && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-stone-500">3. {t("admin_products.sizes_stock_for")} "{selectedStyle ? `${selectedStyle} · ` : ""}{selectedColor}"</Label>
                      <Button type="button" size="sm" variant="ghost" onClick={handleAddAllSizes} className="text-[10px] text-[#8A9A86] h-6 px-2">
                        {locale === "id" ? "Tambah Semua Ukuran" : "Add All Sizes"}
                      </Button>
                    </div>
                    
                    {/* Size chips from master */}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {masterSizes.map((s) => {
                        const isActive = (form.variants || []).some(v => (v.style || "") === (selectedStyle || "") && v.color === selectedColor && v.size === s.name);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => handleToggleSize(s.name)}
                            className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                              isActive
                                ? "bg-[#8A9A86] text-white border-[#8A9A86]"
                                : "bg-white text-stone-500 border-stone-200 hover:border-stone-400"
                            }`}
                          >
                            {s.name}
                          </button>
                        );
                      })}
                      {/* Quick add custom size */}
                      {showNewSizeInput ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={newSizeName}
                            onChange={(e) => setNewSizeName(e.target.value)}
                            placeholder="New"
                            className="w-20 text-xs h-7 px-2"
                            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleQuickAddSize())}
                          />
                          <Button type="button" size="sm" onClick={handleQuickAddSize} variant="default" className="bg-[#8A9A86] h-7 px-1.5"><Check className="w-3 h-3" /></Button>
                          <Button type="button" size="sm" onClick={() => { setShowNewSizeInput(false); setNewSizeName(""); }} variant="outline" className="h-7 px-1.5"><X className="w-3 h-3" /></Button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowNewSizeInput(true)}
                          className="px-3 py-1 text-xs font-medium rounded-full border border-dashed border-stone-300 text-stone-400 hover:text-stone-600 hover:border-stone-400 transition-colors"
                        >
                          <Plus className="w-3 h-3 inline mr-0.5" />{t("admin_products.new_btn")}
                        </button>
                      )}
                    </div>

                    {/* Stock inputs for active sizes */}
                    {getSizesForStyleColor(selectedStyle, selectedColor).length > 0 && (
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                        {getSizesForStyleColor(selectedStyle, selectedColor).map((size) => {
                          const key = `${selectedStyle}|||${selectedColor}|||${size}`;
                          const stock = sizeStock[key] ?? "";
                          const price = sizePrice[key] ?? getDefaultVariantPrice();
                          const weight = sizeWeight[key] ?? getDefaultVariantWeight();
                          const adjustment = sizeAdjustment[key] ?? "0";
                          return (
                            <div key={size} className="flex flex-col gap-2 bg-white border border-stone-200 rounded-lg p-4 shadow-sm min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-stone-700 truncate" title={size}>{size}</span>
                                <button type="button" onClick={() => handleRemoveVariant(selectedStyle, selectedColor, size)} className="text-stone-400 hover:text-red-500" title="Remove"><X className="w-3 h-3" /></button>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
                                <div className="flex-1">
                                  <Label className="text-[10px] text-stone-500">Stok</Label>
                                  <div className="flex items-center mt-1">
                                    <Input type="number" min="0" value={stock} onChange={(e) => handleStockChange(size, e.target.value)} placeholder="0" className="h-7 text-xs" />
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <Label className="text-[10px] text-stone-500">Harga Normal Pusat (Opsional)</Label>
                                  <div className="flex items-center mt-1">
                                    <Input type="number" min="0" value={price} onChange={(e) => handlePriceChange(size, e.target.value)} placeholder="Base price" className="h-7 text-xs" />
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <Label className="text-[10px] text-stone-500">Tambahan Harga Size</Label>
                                  <div className="flex items-center mt-1">
                                    <Input type="number" min="0" value={adjustment} onChange={(e) => handleAdjustmentChange(size, e.target.value)} placeholder="0" className="h-7 text-xs" />
                                  </div>
                                  <p className="mt-1 text-[10px] leading-tight text-stone-400">Ditambahkan setelah diskon role.</p>
                                </div>
                                <div className="flex-1">
                                  <Label className="text-[10px] text-stone-500">Berat (gr)</Label>
                                  <div className="flex items-center mt-1">
                                    <Input type="number" min="1" value={weight} onChange={(e) => handleWeightChange(size, e.target.value)} placeholder="100" className="h-7 text-xs" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Variant Summary Table */}
                {(form.variants || []).length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs text-stone-500">{t("admin_products.variants_summary")}</Label>
                    <div className="max-h-[32rem] overflow-y-auto border border-stone-200 rounded bg-white">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-stone-100 bg-stone-50">
                            <th className="text-left p-2 font-semibold text-stone-500">Style</th>
                            <th className="text-left p-2 font-semibold text-stone-500">{locale === "id" ? "Warna" : "Color"}</th>
                            <th className="text-left p-2 font-semibold text-stone-500">{locale === "id" ? "Ukuran" : "Size"}</th>
                            <th className="text-right p-2 font-semibold text-stone-500">Stok</th>
                            <th className="text-right p-2 font-semibold text-stone-500">Harga Pusat</th>
                            <th className="text-right p-2 font-semibold text-stone-500">Tambahan Size</th>
                            <th className="text-right p-2 font-semibold text-stone-500">Berat</th>
                            <th className="text-center p-2 font-semibold text-stone-500">Diskon</th>
                            <th className="p-2 w-20"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(form.variants || []).map((v, idx) => {
                            const key = `${v.style || ""}|||${v.color || ""}|||${v.size || ""}`;
                            const isEditing = editingVariantKey === key;
                            const isDiscountOpen = discountVariantKey === key;
                            const variantPrice = sizePrice[key] ?? v.price ?? getDefaultVariantPrice();
                            const variantAdjustment = sizeAdjustment[key] ?? v.size_price_adjustment ?? "0";
                            const variantWeight = sizeWeight[key] ?? v.weight_gram ?? getDefaultVariantWeight();
                            const discountStatus = getVariantDiscountStatus(v);
                            return (
                              <Fragment key={key}>
                                <tr key={`${key}-row`} className={`border-b border-stone-50 hover:bg-stone-50/50 ${isEditing || isDiscountOpen ? "bg-[#8A9A86]/5" : ""}`}>
                                  <td className="p-2 text-stone-700">{v.style || "—"}</td>
                                  <td className="p-2 text-stone-700">{v.color || "—"}</td>
                                  <td className="p-2 text-stone-700">{v.size || "—"}</td>
                                  <td className="p-2 text-right">
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        min="0"
                                        value={sizeStock[key] ?? (v.stock === null || v.stock === undefined ? "" : String(v.stock))}
                                        onChange={(e) => handleSummaryStockChange(v.style, v.color, v.size, e.target.value)}
                                        className="w-16 h-6 text-xs text-right border border-stone-300 rounded px-1"
                                      />
                                    ) : (
                                      <span className="text-stone-600">{v.stock || 0}</span>
                                    )}
                                  </td>
                                  <td className="p-2 text-right">
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        min="0"
                                        value={variantPrice}
                                        onChange={(e) => handleSummaryPriceChange(v.style, v.color, v.size, e.target.value)}
                                        placeholder="Base price"
                                        className="w-20 h-6 text-xs text-right border border-stone-300 rounded px-1"
                                      />
                                    ) : (
                                      <span className="text-stone-600">{variantPrice ? formatPrice(variantPrice) : "—"}</span>
                                    )}
                                  </td>
                                  <td className="p-2 text-right">
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        min="0"
                                        value={variantAdjustment}
                                        onChange={(e) => handleSummaryAdjustmentChange(v.style, v.color, v.size, e.target.value)}
                                        placeholder="0"
                                        className="w-20 h-6 text-xs text-right border border-stone-300 rounded px-1"
                                      />
                                    ) : (
                                      <span className="text-stone-600">{variantAdjustment && Number(variantAdjustment) > 0 ? formatPrice(variantAdjustment) : "—"}</span>
                                    )}
                                  </td>
                                  <td className="p-2 text-right">
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        min="1"
                                        value={variantWeight}
                                        onChange={(e) => handleSummaryWeightChange(v.style, v.color, v.size, e.target.value)}
                                        placeholder="100"
                                        className="w-20 h-6 text-xs text-right border border-stone-300 rounded px-1"
                                      />
                                    ) : (
                                      <span className="text-stone-600">{variantWeight ? `${variantWeight} gr` : "—"}</span>
                                    )}
                                  </td>
                                  <td className="p-2 text-center">
                                    <Badge variant={discountStatus.tone === "default" ? "default" : discountStatus.tone === "outline" ? "outline" : "secondary"} className="text-[10px] whitespace-nowrap">
                                      {discountStatus.label}
                                    </Badge>
                                  </td>
                                  <td className="p-2">
                                    <div className="flex items-center justify-end gap-1">
                                      {isEditing ? (
                                        <button
                                          type="button"
                                          onClick={() => setEditingVariantKey(null)}
                                          className="text-[#8A9A86] hover:text-[#768573]"
                                          title="Done"
                                        >
                                          <Check className="w-3 h-3" />
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => setEditingVariantKey(key)}
                                          className="text-stone-400 hover:text-[#8A9A86]"
                                          title="Edit"
                                        >
                                          <PenSquare className="w-3 h-3" />
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => handleToggleVariantDiscount(key)}
                                        className={`text-[11px] font-semibold px-1.5 py-0.5 rounded border ${isDiscountOpen ? "text-[#8A9A86] border-[#8A9A86] bg-[#8A9A86]/10" : "text-stone-500 border-stone-300 hover:border-[#8A9A86] hover:text-[#8A9A86]"}`}
                                        title="Atur Diskon Varian"
                                      >
                                        %
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveVariant(v.style, v.color, v.size)}
                                        className="text-stone-400 hover:text-red-500"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                                {isDiscountOpen && (
                                  <tr key={`${key}-discount`} className="border-b border-stone-100 bg-stone-50/70">
                                    <td colSpan={9} className="p-3">
                                      <div className="rounded-lg border border-stone-200 bg-white p-4 space-y-4 shadow-sm">
                                        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                                          <div>
                                            <p className="text-sm font-semibold text-stone-700">Diskon Khusus Varian — {v.style || "—"} · {v.color || "—"} · {v.size || "—"}</p>
                                            <p className="text-[11px] text-stone-500">Kosongkan untuk mengikuti diskon produk/brand. Isi 0 untuk override tanpa diskon.</p>
                                          </div>
                                          <div className="text-right text-[11px] text-stone-500">
                                            <div>Harga varian: <span className="font-medium text-stone-700">{formatPrice(getVariantDiscountBasePrice(v))}</span></div>
                                            <div>Tambahan size: <span className="font-medium text-stone-700">{formatPrice(normalizeNumberInput(v.size_price_adjustment, 0))}</span></div>
                                          </div>
                                        </div>

                                        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                                          {variantDiscountRoles.map(({ key: roleKey, label }) => {
                                            const type = getVariantDiscountType(v, roleKey);
                                            const currentValue = getVariantDiscountValue(v, roleKey);
                                            const amountPreview = getVariantDiscountAmountPreview(v, roleKey);
                                            const percentPreview = getVariantDiscountBasePrice(v) > 0
                                              ? Number(((amountPreview / getVariantDiscountBasePrice(v)) * 100).toFixed(2))
                                              : 0;
                                            const fixedPreview = amountPreview;
                                            return (
                                              <div key={roleKey} className="rounded-md border border-stone-200 p-3 space-y-2">
                                                <div className="flex items-center justify-between gap-2">
                                                  <Label className="text-xs font-semibold uppercase tracking-wide text-stone-600">{label}</Label>
                                                  <Select value={type} onValueChange={(nextType) => handleVariantDiscountTypeChange(v.style, v.color, v.size, roleKey, nextType)}>
                                                    <SelectTrigger className="h-8 w-[96px] text-xs"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                      <SelectItem value="percent">Percent</SelectItem>
                                                      <SelectItem value="fixed">Fixed</SelectItem>
                                                    </SelectContent>
                                                  </Select>
                                                </div>
                                                <Input
                                                  type="number"
                                                  min="0"
                                                  step="0.01"
                                                  value={currentValue}
                                                  onChange={(e) => handleVariantDiscountValueChange(v.style, v.color, v.size, roleKey, e.target.value)}
                                                  onBlur={(e) => handleVariantDiscountValueBlur(v.style, v.color, v.size, roleKey, e.target.value)}
                                                  placeholder={type === "percent" ? "0" : "Rp 0"}
                                                  className="h-8 text-xs"
                                                />
                                                <div className="grid grid-cols-2 gap-2 text-[11px] text-stone-500">
                                                  <div className="rounded bg-stone-50 px-2 py-1">
                                                    ≈ {percentPreview}%
                                                  </div>
                                                  <div className="rounded bg-stone-50 px-2 py-1 text-right">
                                                    ≈ {formatPrice(fixedPreview)}
                                                  </div>
                                                </div>
                                                <p className="text-[11px] text-stone-500">Harga akhir {label.toLowerCase()}: <span className="font-medium text-stone-700">{formatPrice(getVariantNetPricePreview(v, roleKey))}</span></p>
                                              </div>
                                            );
                                          })}
                                        </div>

                                        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-stone-100 pt-3">
                                          <p className="text-[11px] text-stone-500">Ubah nilai persen/fixed sesuai kebutuhan. Preview nominal ↔ persen menyesuaikan harga varian saat ini.</p>
                                          <div className="flex items-center gap-2">
                                            <Button type="button" variant="outline" size="sm" onClick={() => handleResetVariantDiscount(v.style, v.color, v.size)}>
                                              Reset
                                            </Button>
                                            <Button type="button" size="sm" onClick={() => handleSave({ keepOpen: true })} className="bg-[#8A9A86] hover:bg-[#768573]">
                                              Simpan
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <button onClick={handleSave} className="w-full bg-[#8A9A86] text-white py-3 text-sm uppercase tracking-wider hover:bg-[#768573]" data-testid="save-product-btn">
                {editing ? t("admin_products.update_btn") : t("admin_products.create_btn")}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
